import {
	Button,
	Checkbox,
	Divider,
	Group,
	Loader,
	Popover,
	rem,
	SegmentedControl,
	Select,
	SimpleGrid,
	Stack,
	Text,
	UnstyledButton,
} from '@mantine/core';
import { useContext, useMemo, useState } from 'react';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import {
	useTranslationCapChart,
	useTranslationCapTable,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	CAP_TABLE_NUMBER_FORMAT,
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { CapChartPageContext } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPage.tsx';
import { orderValuesHeldTypes } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSettings.utils.ts';
import { getNumberFormatExample } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartSettings/CapChartSettings.utils.ts';
import { asEnumValue } from '@dealstack/frontend/shared/utils/asEnumValue.ts';
import { CapChartSecuritiesPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartSettings/CapChartSecuritiesPanel.tsx';
import type { CapChartSettingsView } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartSettings/CapChartSettings.types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';

const ELEVATION_XL_SHADOW = '0px 16px 32px 0px #91969833';

// The theme fills a checked Select option; this list marks it with a border and bold label only.
const NUMBER_FORMAT_SELECT_STYLES = {
	dropdown: {
		borderColor: 'var(--mantine-color-gray-2)',
		boxShadow: ELEVATION_XL_SHADOW,
	},
	option: {
		border: '1px solid transparent',
		// Doubled so it outranks the theme's checked-option rule.
		'&&[data-checked]': {
			borderColor: 'var(--mantine-color-blue-4)',
		},
	},
	groupLabel: { display: 'none' },
	group: {
		'& + &': {
			borderTop: '1px solid var(--mantine-color-gray-2)',
			marginTop: rem(2),
			paddingTop: rem(2),
		},
	},
};

// Last row in the popover, so its hover fill follows the popover's rounded corners.
const SECURITIES_ROW_STYLES = {
	root: {
		borderRadius: `0 0 ${rem(7)} ${rem(7)}`,
		'&:hover:not(:disabled)': { backgroundColor: 'var(--mantine-color-gray-0)' },
		'&:disabled': { cursor: 'not-allowed', opacity: 0.4 },
	},
};

const SEGMENTED_CONTROL_STYLES = {
	indicator: {
		border: '1px solid var(--mantine-color-blue-6)',
		borderRadius: rem(4),
		boxShadow: 'none',
	},
};

export const CapChartSettings = () => {
	const tCapChart = useTranslationCapChart();
	const tCapTable = useTranslationCapTable();
	const [view, setView] = useState<CapChartSettingsView>('settings');
	const [isOpened, setIsOpened] = useState(false);

	const capChartContext = useContext(CapChartPageContext);
	const {
		onValuesHeldTypesChange,
		onValueDisplayModeChange,
		onNumberFormatChange,
		onResetSettings,
		chartSettings,
	} = capChartContext.capChartData;

	const {
		chartSecurities,
		selectedSecurities,
		selectedSecuritiesCount,
		totalSecuritiesCount,
		isLoadingSecurities,
		isFilteringSecurities,
		setSecuritiesSelected,
		selectAllSecurities,
	} = capChartContext.capChartSecurities;

	const showPercentages =
		chartSettings.valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE;

	const valueOptions = useMemo(
		() => [
			{ value: CAP_TABLE_VALUES_HELD_TYPE.SECURITIES, label: tCapChart('values.units') },
			{ value: CAP_TABLE_VALUES_HELD_TYPE.VOTES, label: tCapChart('values.votes') },
		],
		[tCapChart]
	);

	const displayModeOptions = useMemo(
		() => [
			{
				value: CAP_TABLE_VALUE_DISPLAY_MODE.ABSOLUTE,
				label: (
					<Text size="B6Medium" c="dark.8">
						{tCapChart('settings.display_as.number')}
					</Text>
				),
			},
			{
				value: CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE,
				label: (
					<Text size="B6Medium" c="dark.8">
						{tCapChart('settings.display_as.percentage')}
					</Text>
				),
			},
		],
		[tCapChart]
	);

	// Group names are keys only; the labels are hidden and the second group draws the divider.
	const numberFormatOptions = useMemo(
		() => [
			{
				group: 'unscaled',
				items: [
					{
						value: CAP_TABLE_NUMBER_FORMAT.FULL,
						label: tCapChart('settings.number_format.full'),
					},
					{
						value: CAP_TABLE_NUMBER_FORMAT.SHORTENED,
						label: tCapChart('settings.number_format.shortened'),
					},
				],
			},
			{
				group: 'scaled',
				items: [
					{
						value: CAP_TABLE_NUMBER_FORMAT.THOUSANDS,
						label: tCapChart('settings.number_format.thousands'),
					},
					{
						value: CAP_TABLE_NUMBER_FORMAT.MILLIONS,
						label: tCapChart('settings.number_format.millions'),
					},
					{
						value: CAP_TABLE_NUMBER_FORMAT.BILLIONS,
						label: tCapChart('settings.number_format.billions'),
					},
				],
			},
		],
		[tCapChart]
	);

	const handleValueDisplayModeChange = (value: string) => {
		const valueDisplayMode = asEnumValue(Object.values(CAP_TABLE_VALUE_DISPLAY_MODE), value);
		if (valueDisplayMode) {
			onValueDisplayModeChange(valueDisplayMode);
		}
	};

	const handleNumberFormatChange = (value: string | null) => {
		const numberFormat = asEnumValue(Object.values(CAP_TABLE_NUMBER_FORMAT), value);
		if (numberFormat) {
			onNumberFormatChange(numberFormat);
		}
	};

	const handleReset = () => {
		selectAllSecurities();
		onResetSettings();
	};

	const securitiesCountLabel = isFilteringSecurities
		? tCapChart('settings.securities_count.some', {
				selected: selectedSecuritiesCount,
				total: totalSecuritiesCount,
			})
		: tCapChart('settings.securities_count.all', { count: totalSecuritiesCount });

	return (
		<Popover
			width={344}
			position="bottom-end"
			radius={8}
			withinPortal
			onOpen={() => {
				setView('settings');
				setIsOpened(true);
			}}
			onClose={() => setIsOpened(false)}
		>
			<Popover.Target>
				<Button
					variant="default"
					bg={isOpened ? 'gray.2' : undefined}
					data-testid={TEST_ELEMENT_IDS.GSC_SETTINGS_BUTTON}
					aria-label={tCapChart('settings.title')}
				>
					<Icon variant="settings-filter" size="vlg" />
				</Button>
			</Popover.Target>
			<Popover.Dropdown
				p={0}
				bd="1px solid gray.0"
				style={{ boxShadow: ELEVATION_XL_SHADOW }}
			>
				{view === 'securities' ? (
					<CapChartSecuritiesPanel
						securities={chartSecurities ?? []}
						selectedSecurities={selectedSecurities}
						selectedCount={selectedSecuritiesCount}
						totalCount={totalSecuritiesCount}
						onSecuritiesSelected={setSecuritiesSelected}
						onSelectAll={selectAllSecurities}
						onBack={() => setView('settings')}
					/>
				) : (
					<>
						<Group justify="space-between" wrap="nowrap" pl={16} pr={12} py={8}>
							<Text size="B6Bold" c="dark.8">
								{tCapChart('settings.title')}
							</Text>
							<UnstyledButton h={36} onClick={handleReset}>
								<Text size="B6Bold" c="blue.6">
									{tCapChart('settings.reset')}
								</Text>
							</UnstyledButton>
						</Group>
						<Divider color="gray.2" />

						<Stack gap={14} px={16} pt={14} pb={16}>
							<Stack gap={8}>
								<Group justify="space-between" wrap="nowrap">
									<Text fz="sm" fw={600} lh={rem(16)} c="dark.8">
										{tCapChart('settings.values_label')}
									</Text>
									<Text fz="sm" lh={rem(16)} c="gray.9">
										{tCapChart('settings.values_hint')}
									</Text>
								</Group>
								<Checkbox.Group
									value={chartSettings.valuesHeldTypes}
									onChange={(next) =>
										onValuesHeldTypesChange(orderValuesHeldTypes(next))
									}
								>
									<SimpleGrid
										cols={2}
										spacing={2}
										p={3}
										bd="1px solid gray.4"
										style={{ borderRadius: rem(6) }}
									>
										{valueOptions.map(({ value, label }) => {
											const checked =
												chartSettings.valuesHeldTypes.includes(value);
											const isLastSelected =
												checked &&
												chartSettings.valuesHeldTypes.length === 1;
											return (
												<Checkbox.Card
													key={value}
													value={value}
													disabled={isLastSelected}
													withBorder={false}
													radius={4}
													h={30}
													px={12}
													bg={checked ? 'blue.0' : undefined}
													bd={`1px solid ${checked ? 'blue.6' : 'transparent'}`}
												>
													<Group gap={8} justify="center" wrap="nowrap">
														<Checkbox.Indicator
															size="xs"
															color="blue.6"
															disabled={isLastSelected}
														/>
														<Text size="B6Medium" c="dark.8">
															{label}
														</Text>
													</Group>
												</Checkbox.Card>
											);
										})}
									</SimpleGrid>
								</Checkbox.Group>
							</Stack>

							<Stack gap={8}>
								<Text fz="sm" fw={600} lh={rem(16)} c="dark.8">
									{tCapChart('settings.display_as_label')}
								</Text>
								<SegmentedControl
									fullWidth
									value={chartSettings.valueDisplayMode}
									onChange={handleValueDisplayModeChange}
									data={displayModeOptions}
									withItemsBorders={false}
									color="blue.0"
									radius={6}
									p={3}
									styles={SEGMENTED_CONTROL_STYLES}
								/>
							</Stack>

							<Stack gap={8}>
								<Text fz="sm" fw={600} lh={rem(16)} c="dark.8">
									{tCapChart('settings.number_format_label')}
								</Text>
								<Select
									aria-label={tCapChart('settings.number_format_label')}
									data={numberFormatOptions}
									value={chartSettings.numberFormat}
									onChange={handleNumberFormatChange}
									allowDeselect={false}
									disabled={showPercentages}
									rightSection={<Icon variant="caret-down" size="lg" />}
									rightSectionPointerEvents="none"
									// Kept inside the popover so picking an option is not an outside click.
									comboboxProps={{
										withinPortal: false,
										radius: 6,
										dropdownPadding: 4,
									}}
									styles={NUMBER_FORMAT_SELECT_STYLES}
									renderOption={({ option, checked }) => (
										<Group justify="space-between" wrap="nowrap" w="100%">
											<Text
												size="B6Medium"
												fw={checked ? 600 : 400}
												c="dark.8"
											>
												{option.label}
											</Text>
											<Text fz="sm" lh={rem(16)} c="gray.9">
												{getNumberFormatExample(option.value)}
											</Text>
										</Group>
									)}
									data-testid={TEST_ELEMENT_IDS.GSC_SETTINGS_NUMBER_FORMAT_SELECT}
								/>
							</Stack>
						</Stack>

						<Divider color="gray.2" />
						<UnstyledButton
							w="100%"
							px={16}
							py={12}
							disabled={totalSecuritiesCount === 0}
							styles={SECURITIES_ROW_STYLES}
							onClick={() => setView('securities')}
						>
							<Group gap={10} wrap="nowrap">
								<Icon variant="table-v3" size="lg" />
								<Text size="B6Medium" c="dark.8" flex={1} miw={0} truncate>
									{tCapTable('header_actions.specify_securities')}
								</Text>
								{isLoadingSecurities && <Loader size="xs" />}
								{totalSecuritiesCount > 0 && (
									<Text size="B6Medium" c="gray.9">
										{securitiesCountLabel}
									</Text>
								)}
								<Icon
									variant="chevron-right-v2"
									size="md"
									color="var(--mantine-color-dark-0)"
								/>
							</Group>
						</UnstyledButton>
					</>
				)}
			</Popover.Dropdown>
		</Popover>
	);
};
